//MMT3 Reports
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { toast } from 'react-toastify';
import { GeneralError } from '../../Utility/Constants';
import { Refresh, AccessTime } from '@mui/icons-material';
import { Button } from '@mui/material';
import { MmtSkipDetailsColumns, MmtLoadDetailsColumns, MmtManualDetailsColumns, MmtCompoundDetailsColumns } from "../../Table/TableColumn";
import {
    triggerDatabricksJobData
} from '../../Redux/DataBricks/MMTDataBricksSlice';
import {
    clearData,
    getMmtSkipDetailsData,
    getMmtLoadDetailsData,
    getMmtManualDetailsData,
    getMmtCompoundDetailsData
} from '../../Redux/MMTReports/MMT3Slice';
import Spinner from '../../Spinner/Spinner';
import ReportsTable from '../../Table/ReportsTable';
import { getAccessToken } from '../../Redux/TokenSlice';
import { useMemo } from 'react';

function MMT3() {
    const dispatch = useDispatch();
    const lastRefreshedDate = useSelector(state => state.mmt3.loadData.lastRefreshedDate);
    const isLoadDataLoading = useSelector(state => state.mmt3.loadData.loading);
    const isSkipDataLoading = useSelector(state => state.mmt3.skipData.loading);
    const isManualDataLoading = useSelector(state => state.mmt3.manualData.loading);
    const isCompoundDataLoading = useSelector(state => state.mmt3.compoundData.loading);
    const loadDataError = useSelector(state => state.mmt3.loadData.error);
    const skipDataError = useSelector(state => state.mmt3.skipData.error);
    const manualDataError = useSelector(state => state.mmt3.manualData.error);
    const compoundDataError = useSelector(state => state.mmt3.compoundData.error);

    // Add a flag to track if initial fetch has been attempted
    const [initialFetchDone, setInitialFetchDone] = React.useState(false);
    const [refreshed, setRefreshed] = React.useState(false);

    const memoizedLoadColumns = useMemo(() => MmtLoadDetailsColumns, []);
    const memoizedSkipColumns = useMemo(() => MmtSkipDetailsColumns, []);
    const memoizedManualColumns = useMemo(() => MmtManualDetailsColumns, []);
    const memoizedCompoundColumns = useMemo(() => MmtCompoundDetailsColumns, []);

    // MMT-3 table configuration
    const mmt3TableConfig = {
        tables: ['Load Details', 'Skip Details', 'Manual Details', 'Compound Details'],
        titles: ['Load Details', 'Skip Details', 'Manual Details', 'Compound Details'],
        columns: [memoizedLoadColumns, memoizedSkipColumns, memoizedManualColumns, memoizedCompoundColumns],
        //columns: [MmtLoadDetailsColumns, MmtSkipDetailsColumns, MmtManualDetailsColumns, MmtCompoundDetailsColumns],
        loading: [isLoadDataLoading, isSkipDataLoading, isManualDataLoading, isCompoundDataLoading]
    };

    useEffect(() => {
        if (loadDataError || skipDataError || manualDataError || compoundDataError) {
            toast.error(GeneralError);
            return;
        }
    }, [loadDataError, skipDataError, manualDataError, compoundDataError]);

    const handleRefresh = async (value) => {
        try {
            setRefreshed(true)
            switch (value) {
                case 'Load Details': {
                    await dispatch(getMmtLoadDetailsData());
                    break;
                }
                case 'Skip Details':
                    await dispatch(getMmtSkipDetailsData());
                    break;
                case 'Manual Details':
                    await dispatch(getMmtManualDetailsData());
                    break;
                case 'Compound Details':
                    await dispatch(getMmtCompoundDetailsData());
                    break;
                default:
                    break;
            }
        }
        catch (error) {
            console.error('Error refreshing MMT3 data:', error);
            toast.error('Failed to refresh MMT3 data');
        }
        finally {
            setRefreshed(false);
        }
    }

    useEffect(() => {
        // Only run on first mount
        if (initialFetchDone) return;

        const fetchAllMMT3Data = async () => {
            try {
                // Clear existing data first
                await dispatch(clearData());
                // Get access token and then fetch data
                await dispatch(getAccessToken());
                // Fetch fresh data
                await Promise.all([dispatch(getMmtLoadDetailsData()),
                dispatch(getMmtSkipDetailsData()),
                dispatch(getMmtManualDetailsData()),
                dispatch(getMmtCompoundDetailsData())
                ]);
                setInitialFetchDone(true);
            } catch (error) {
                console.error('Error fetching MMT3 data:', error);
                toast.error('Failed to fetch MMT3 data');
                setInitialFetchDone(true);
            }
        };
        fetchAllMMT3Data();
    }, [dispatch, initialFetchDone]);

    // Handle new run execution DataBricks - no carrier selection needed
    const handleRunDataBricksJobSecondary = async () => {
        try {
            const action = await dispatch(triggerDatabricksJobData(2));
            if (triggerDatabricksJobData.fulfilled.match(action)) {
                const runId = action.payload.runId ?? 'N/A';
                toast.success(`Databricks job started (Run ID: ${runId})`);
            } else if (triggerDatabricksJobData.rejected.match(action)) {
                toast.error(action.payload || 'Failed to start Databricks job');
            }
        }
        catch (error) {
            toast.error('Failed to trigger Databricks job');
            console.error('Error triggering Databricks job:', error);
        }
    };

    const renderMMTTables = () => {
        return (
            <>
                <Box sx={{ margin: '20px 0' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleRunDataBricksJobSecondary}
                        sx={{ marginLeft: '10px' }}
                    >
                        Start New Run
                    </Button>
                </Box>
                {initialFetchDone && mmt3TableConfig.tables.map((tableName, index) => {
                    const dataSource = getDataSourceForTable(index);
                    return (
                        <div key={tableName} style={{ marginTop: index > 0 ? '40px' : '0px' }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                paddingBottom: '16px',
                                gap: 2
                            }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={true}
                                    startIcon={<AccessTime />}
                                    sx={{
                                        minWidth: '120px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {`Last Updated: ${lastRefreshedDate ? lastRefreshedDate.split('.')[0] : ''}`}
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleRefresh(mmt3TableConfig.titles[index])}
                                    disabled={mmt3TableConfig.loading[index]}
                                    startIcon={<Refresh />}
                                    sx={{
                                        minWidth: '120px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {mmt3TableConfig.loading[index] ? 'Refreshing...' : 'Refresh'}
                                </Button>
                            </Box>
                            <ReportsTable
                                tableName={tableName}
                                tableIndex={index}
                                columns={mmt3TableConfig.columns[index]}
                                dataSource={dataSource}
                                loading={mmt3TableConfig.loading[index]}
                                sliceName={'mmt3'}
                            />
                        </div>
                    );
                })}
            </>
        );
    };

    // Map table index to data source from MMT3Slice
    const getDataSourceForTable = (tableIndex) => {
        switch (tableIndex) {
            case 0: return 'loadData';    // MMT3_loads
            case 1: return 'skipData';    // MMT3_skips  
            case 2: return 'manualData';  // MMT3_manuals
            case 3: return 'compoundData'; // MMT3_compounds
            default: return '';
        }
    };

    return (
        <>
            {!refreshed && (!initialFetchDone || (isLoadDataLoading ||
                isSkipDataLoading || isManualDataLoading ||
                isCompoundDataLoading))
                && <Spinner />}
            {renderMMTTables()}
        </>
    );
}

export default MMT3;